import React, { useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import SuccessModal from './SuccessModal';

const ConsentUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const FileUploadWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 56px;
  background: #FFFFFF;
  border: 1px solid #F0F0F0;
  border-radius: 12px;
`;

const FileUploadText = styled.span`
  font-family: 'Sofia Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #6D6D71;
`;

const FileUploadIconWrapper = styled.label`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 32px;
  height: 32px;
  border: 1px solid #A7A7AA;
  border-radius: 4px;
  cursor: pointer;
`;

const FileUploadIcon = styled.div`
  width: 16px;
  height: 16px;
  border: solid 2px #6D6D71;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
  margin-top: -4px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileName = styled.p`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  margin-top: 8px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: ${props => props.disabled ? '#E0F0FF' : '#2575BD'};
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: ${props => props.disabled ? '#91D3FF' : '#FFFFFF'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  margin-top: 20px;
`;

const ConsentUpload = ({ participantId, onSaveSuccess }) => {
  const [file, setFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const uploadWithPresignedUrl = async (file) => {
    const token = localStorage.getItem('token');

    try {
      // Get presigned POST data from backend
      const presignedResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}participant/get-presigned-url/`,
        {
          participant_id: participantId,
          file_type: 'consent_file',
          file_name: file.name,
          content_type: file.type
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { presigned_post, file_url } = presignedResponse.data;

      // Create FormData for POST upload
      const formData = new FormData();

      // Add all the fields from presigned POST
      Object.keys(presigned_post.fields).forEach(key => {
        formData.append(key, presigned_post.fields[key]);
      });

      // Add the file last
      formData.append('file', file);

      // Upload file directly to S3 using presigned POST
      await axios.post(presigned_post.url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return file_url;
    } catch (error) {
      console.error('Error uploading with presigned POST:', error);

      // Handle specific S3 errors
      if (error.response) {
        const errorData = error.response.data;

        // Parse XML error response from S3
        if (typeof errorData === 'string' && errorData.includes('<Error>')) {
          if (errorData.includes('<Code>EntityTooLarge</Code>')) {
            throw new Error('File is too large. Maximum file size allowed is 1 GB.');
          } else if (errorData.includes('<Code>InvalidArgument</Code>')) {
            throw new Error('Invalid file type. Please check the file format.');
          } else if (errorData.includes('<Code>AccessDenied</Code>')) {
            throw new Error('Access denied. Please check your permissions.');
          } else if (errorData.includes('<Code>SignatureDoesNotMatch</Code>')) {
            throw new Error('Upload session expired. Please try again.');
          } else {
            // Extract error message from XML if possible
            const messageMatch = errorData.match(/<Message>(.*?)<\/Message>/);
            const message = messageMatch ? messageMatch[1] : 'Upload failed';
            throw new Error(`Upload failed: ${message}`);
          }
        } else {
          throw new Error(`Upload failed with status ${error.response.status}: ${error.response.statusText}`);
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your internet connection and try again.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  const updateMongoDBWithFileUrl = async (url) => {
    const token = localStorage.getItem('token');

    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}participant/upload/consent/`,
      {
        participant_id: participantId,
        file_url: url
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const handleSave = async () => {
    if (!file) {
      alert('Please select a file to upload');
      return;
    }

    setIsUploading(true);

    try {
      // Upload using presigned URL
      const fileUrl = await uploadWithPresignedUrl(file);

      // Update MongoDB with file URL
      await updateMongoDBWithFileUrl(fileUrl);

      setSuccessMessage(`Consent form successfully uploaded for Participant ID: ${participantId}`);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error uploading consent form:', error);
      alert(error.message || 'Error uploading consent form. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    onSaveSuccess();
  };

  return (
    <ConsentUploadContainer>
      <SectionContainer>
        <SectionTitle>CONSENT FORM UPLOAD</SectionTitle>
        <ContentBox>
          <FileUploadWrapper>
            <FileUploadText>
              {file ? 'File selected' : 'Choose Consent Form'}
            </FileUploadText>
            <FileUploadIconWrapper htmlFor="consent-file-input">
              <FileUploadIcon />
            </FileUploadIconWrapper>
          </FileUploadWrapper>
          <FileInput
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            id="consent-file-input"
          />
          {file && <FileName>{file.name}</FileName>}
        </ContentBox>
      </SectionContainer>

      <SaveButton onClick={handleSave} disabled={!file || isUploading}>
        {isUploading ? 'Uploading...' : 'Save and Next'}
      </SaveButton>

      {isModalOpen && (
        <SuccessModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title="Success"
          message={successMessage}
        />
      )}
    </ConsentUploadContainer>
  );
};

export default ConsentUpload;
